import { Avatar, Button, Card, Empty, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, UserOutlined } from "@ant-design/icons";
import {
  formatDateTime,
  membershipUserEmail,
  membershipUserName,
  readable,
  statusColor,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceStyles } from "./colors";

const { Text } = Typography;

const directPhotoUrl = (membership) => {
  const value =
    membership?.user?.profilePhoto ||
    membership?.user?.photo ||
    membership?.user?.avatar ||
    "";
  const raw = String(value || "").trim();
  return /^(https?:|data:|blob:)/i.test(raw) ? raw : "";
};

const WorkspaceMemberCard = ({
  membership,
  title,
  compact = false,
  highlight = false,
  canRemove = false,
  onRemove,
  onView,
  removing = false,
}) => {
  const roles = membership?.roleNames || [];
  const privileges = membership?.privilegeScopes || [];
  const name = membershipUserName(membership);
  const email = membershipUserEmail(membership);
  const createdAt = formatDateTime(membership?.createdAt);
  const photoUrl = directPhotoUrl(membership);

  return (
    <Card
      size="small"
      hoverable={Boolean(onView)}
      onClick={onView}
      style={{
        borderColor: highlight ? OLIVEEB_COLORS.goldBorder : OLIVEEB_COLORS.border,
        background: highlight ? OLIVEEB_COLORS.goldSoft : OLIVEEB_COLORS.card,
        borderRadius: 8,
        width: "100%",
        cursor: onView ? "pointer" : "default",
      }}
      styles={{ body: { padding: compact ? 12 : 16 } }}
    >
      <Space direction="vertical" size={compact ? 6 : 10} style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%" }}>
          <Avatar
            src={photoUrl || undefined}
            icon={!photoUrl ? <UserOutlined /> : undefined}
            size={compact ? 30 : 38}
            style={{
              background: workspaceStyles.iconBubble.background,
              color: workspaceStyles.iconBubble.color,
              flex: "0 0 auto",
            }}
          >
            {!photoUrl ? String(name || "U").slice(0, 1).toUpperCase() : null}
          </Avatar>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
              <Text
                strong
                style={{
                  display: "block",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  flex: "1 1 180px",
                }}
              >
                {title || name}
              </Text>
              <Tag color={statusColor(membership?.membershipStatus)} style={{ marginInlineEnd: 0 }}>
                {readable(membership?.membershipStatus, "Membership")}
              </Tag>
              {canRemove ? (
                <Popconfirm
                  title="Remove member?"
                  description="This member will lose access to the workspace."
                  okText="Remove"
                  okButtonProps={{ danger: true, loading: removing }}
                  onConfirm={(event) => {
                    event?.stopPropagation?.();
                    return onRemove?.(membership);
                  }}
                >
                  <Tooltip title="Remove member">
                    <Button
                      danger
                      size="small"
                      type="text"
                      icon={<DeleteOutlined />}
                      loading={removing}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </Tooltip>
                </Popconfirm>
              ) : null}
            </div>
            {(title && name && name !== title) || email ? (
              <Text
                type="secondary"
                style={{ display: "block", fontSize: 12, wordBreak: "break-word" }}
              >
                {[title && name && name !== title ? name : "", email].filter(Boolean).join(" • ")}
              </Text>
            ) : null}
          </div>
        </div>

        {roles.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {roles.map((role) => (
              <Tag key={role} color="blue" style={{ marginInlineEnd: 0 }}>
                {readable(role)}
              </Tag>
            ))}
          </div>
        ) : null}

        {privileges.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {privileges.map((scope) => (
              <Tag key={scope} color="cyan" style={{ marginInlineEnd: 0 }}>
                {readable(scope)}
              </Tag>
            ))}
          </div>
        ) : null}

        {createdAt && !compact ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Joined {createdAt}
          </Text>
        ) : null}
      </Space>
    </Card>
  );
};

export const WorkspaceMembers = ({
  members = [],
  emptyText = "No members found.",
  canRemoveMember,
  onRemoveMember,
  onViewMember,
  removingMemberId,
}) => {
  if (!members.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  }

  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      {members.map((member) => (
        <WorkspaceMemberCard
          key={member.id || member.userId || member?.user?.id}
          membership={member}
          title={member.displayTitle}
          compact
          canRemove={Boolean(canRemoveMember?.(member))}
          removing={removingMemberId === member.id}
          onRemove={onRemoveMember}
          onView={onViewMember ? () => onViewMember(member) : undefined}
        />
      ))}
    </Space>
  );
};

export default WorkspaceMemberCard;
